import AxiosInstance from "./AxiosInstance";
import {
  AUTH,
  INVOICE_DATA,
  PARTNER,
  ANNOUNCEMENT,
  ANNOUNCEMENT_PROMOTION,
  PARTNER_ANNOUNCEMENTS,
  PARTNER_ANNOUNCEMENT_COUNTS,
  ANNOUNCEMENTS,
  ANNOUNCEMENT_DETAILS,
  DELETE_ANNOUNCEMENT,
  UPDATE_ANNOUNCEMENT,
  CHANGE_ANNOUNCEMENT_HIDDEN_STATUS,
  PAYMENT,
  SUPPLIES,
  EDUCATION,
  PARTNER_ANNOUNCEMENTS_COUNT_PER_PHONE_NUMBER,
  DISCOUNT_CODE,
  CHANGE_EMAIL_REQUEST,
  CONFIRM_EMAIL_CHANGE_CURRENT,
  CONFIRM_EMAIL_CHANGE_NEW,
  GET_PRICE_FOR_COUNTRY,
  GET_MOST_POPULAR_AND_LATEST,
  GET_PLACE_NAME,
  USER,
} from "./endpoints";
import {
  GetPartnerAnnouncementCountsResponse,
  GetPartnerInfoResponse,
  GetUserResponse,
  PatchInvoiceDataRequest,
  SignUpRequest,
  SignUpResponse,
  GetAnnouncementPromotionResponse,
  GetAnnouncementDetailsResponse,
  GetAnnouncementsResponse,
  GetPagedAnnouncementsResponse,
  RegisterTransactionResponse,
  PostAnnoucementResponse,
  GetSuppliesResponse,
  GetEducationAnnouncementsResponse,
  GetPartnerAnnouncementsCountPerPhoneNumberResponse,
  CheckDiscountCodeResponse,
  PutAnnoucementResponse,
  PopularAndLatestAnnouncements,
} from "./responseRequestTypes";
import {
  Announcement,
  PriceInformation,
} from "utils/interfaces/commonInterfacesAndTypes";
import { AxiosResponse } from "axios";
import { NamedPlace } from "utils/interfaces/place";
import RegisterUserForm from "../../user/RegisterUserForm";

class HTTPService {
  private axios = new AxiosInstance();

  public signUp = (userData: RegisterUserForm) =>
    this.axios.post<void>(USER, userData);

  public getUser = () => this.axios.get<GetUserResponse>(USER);

  public getPartner = () => this.axios.get<GetPartnerInfoResponse>(PARTNER);

  public patchInvoiceData = (invoiceData: PatchInvoiceDataRequest) =>
    this.axios.patch(INVOICE_DATA, invoiceData);

  public postAnnoucement = (announcement: Announcement) =>
    this.axios.post<PostAnnoucementResponse>(ANNOUNCEMENT, announcement);

  public getPartnerAnnouncements = (status: string) =>
    this.axios.get<GetAnnouncementsResponse>(PARTNER_ANNOUNCEMENTS, {
      params: { status },
    });

  public getPartnerAnnouncementCounts = () =>
    this.axios.get<GetPartnerAnnouncementCountsResponse>(
      PARTNER_ANNOUNCEMENT_COUNTS
    );

  public getPartnerAnnouncementsCountByPhoneNumber = (phoneNumber: string) =>
    this.axios.get<GetPartnerAnnouncementsCountPerPhoneNumberResponse>(
      PARTNER_ANNOUNCEMENTS_COUNT_PER_PHONE_NUMBER,
      { params: { phoneNumber } }
    );

  public getAnnouncementPromotion = () =>
    this.axios.get<GetAnnouncementPromotionResponse>(ANNOUNCEMENT_PROMOTION);

  public getAnnouncementDetails = (id: string) =>
    this.axios.get<GetAnnouncementDetailsResponse>(ANNOUNCEMENT_DETAILS(id));

  public getAnnouncements = (filters: any, pageNr: number) =>
    this.axios.post<GetPagedAnnouncementsResponse>(ANNOUNCEMENTS, filters, {
      params: {
        pageNr,
      },
    });

  public putAnnouncement = (
    id: string,
    updatedAnnouncement: Announcement,
    extendOrActivateEdition = false
  ) =>
    this.axios.put<PutAnnoucementResponse>(
      UPDATE_ANNOUNCEMENT(id),
      updatedAnnouncement,
      {
        params: { extendOrActivateEdition },
      }
    );

  public getImage = (imageUrl: string) =>
    this.axios.get(imageUrl, {
      responseType: "arraybuffer",
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
        Expires: "0",
      },
      transformRequest: (data, headers) => {
        delete headers!["Authorization"];
      },
    });

  public deleteAnnouncement = (id: string) =>
    this.axios.delete(DELETE_ANNOUNCEMENT(id));

  public changeAnnouncementHiddenStatus = (id: string, hidden: boolean) =>
    this.axios.post(CHANGE_ANNOUNCEMENT_HIDDEN_STATUS(id), { hidden });

  public deletePartnerAccount = () => this.axios.delete(AUTH);

  public registerTransaction = (id: string, language?: string) =>
    this.axios.post<RegisterTransactionResponse>(
      PAYMENT,
      { announcementId: id },
      { headers: { "User-Language": language || "en" } }
    );

  public getSupplies = () => this.axios.get<GetSuppliesResponse>(SUPPLIES);

  public getEducationAnnouncements = () =>
    this.axios.get<GetEducationAnnouncementsResponse>(EDUCATION);

  public checkDiscountCode = (discountCode: string) =>
    this.axios.get<CheckDiscountCodeResponse>(DISCOUNT_CODE, {
      params: { code: discountCode },
    });

  public requestEmailChange = (currentEmail: string, newEmail: string) =>
    this.axios.post(CHANGE_EMAIL_REQUEST, {
      currentEmail: currentEmail,
      newEmail: newEmail,
    });

  public confirmCurrentEmailChange: (
    code: string
  ) => Promise<AxiosResponse<void>> = (requestId) =>
    this.axios.post(
      CONFIRM_EMAIL_CHANGE_CURRENT,
      {},
      { params: { requestId: requestId } }
    );

  public confirmNewEmailChange: (code: string) => Promise<AxiosResponse<void>> =
    (requestId) =>
      this.axios.post(
        CONFIRM_EMAIL_CHANGE_NEW,
        {},
        { params: { requestId: requestId } }
      );

  public getPriceForCountry = (countryCode: string) =>
    this.axios.get<PriceInformation>(GET_PRICE_FOR_COUNTRY, {
      params: { countryCode },
    });

  public getPopularAndLatest = () =>
    this.axios.get<PopularAndLatestAnnouncements>(GET_MOST_POPULAR_AND_LATEST);

  public getPlaceName = (placeId: string) =>
    this.axios.get<NamedPlace>(GET_PLACE_NAME, { params: { placeId } });
}

export default new HTTPService();
